import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { useForm, Controller } from "react-hook-form";
import { useLoginMutation } from "../../hooks/queries/useAuthQueries";
import { toastService } from "../../providers";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { loginValidators } from "../../lib/validations/authValidations";
import useAuthStore from "../../stores/authStore";
import { AUTH_STEP_UP_CODES } from "../../components/auth/config/authActionCodes";
import { usePinLoginBlockStore } from "../../stores/useOtpBlockStore";
import { useOtpBlockCountdown } from "../../hooks/useOtpBlockCountdown";
import { OtpBlockBanner } from "../../components/shared/OtpBlockBanner";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import CustomCheckBox from "../../components/ui/CustomCheckBox/CustomCheckBox";
import CustomTextInput from "../../components/ui/CustomTextInput/CustomTextInput";
import CustomPasswordInput from "../../components/ui/CustomPasswordInput/CustomPasswordInput";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import CustomNotificationBanner from "../../components/ui/CustomNotificationBanner/CustomNotificationBanner";
import { useLocationBanner } from "./hooks/useLocationBanner";

function LoginPage() {
  const navigate = useNavigate();
  const { banner, dismissBanner } = useLocationBanner();
  const { variant, title, message } = banner ?? {};
  const { mutate: loginMutation, isPending } = useLoginMutation();
  const { setTokens, setTempEmail, setPinChallenge } = useAuthStore();
  const { isBlocked, countdown } = useOtpBlockCountdown(usePinLoginBlockStore);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onTouched",
  });

  const email = watch("email");
  const password = watch("password");
  const hasFormErrors =
    Boolean(loginValidators.email(email)) ||
    Boolean(loginValidators.password(password));

  const forgotPassword = (
    <div className="grid grid-cols-1 gap-2 mt-2 text-xs sm:text-sm text-neutral-500 sm:flex sm:items-center sm:justify-between">
      <CustomCheckBox label="Remember me" disabled={isBlocked} />
      {isBlocked ? (
        <span className="text-neutral-400 cursor-not-allowed md:ml-4">
          Forgot Password?
        </span>
      ) : (
        <a
          href="/auth/request-password-reset"
          className="text-primary-500 hover:underline md:ml-4"
        >
          Forgot Password?
        </a>
      )}
    </div>
  );

  const onSubmit = (values) => {
    loginMutation(values, {
      onSuccess: (data) => {
        const stepUpCode = data.action?.code;

        if (stepUpCode === AUTH_STEP_UP_CODES.PIN_REQUIRED) {
          setPinChallenge(
            data.action?.challengeId ?? data.challengeId,
            data.action?.expiresAt ?? data.expiresAt,
          );
          navigate("/auth/verify-pin");
        } else if (stepUpCode === AUTH_STEP_UP_CODES.PIN_SETUP_REQUIRED) {
          setPinChallenge(
            data.action?.challengeId ?? data.challengeId,
            data.action?.expiresAt ?? data.expiresAt,
          );
          navigate("/auth/setup-pin");
        } else if (data.mfaEnabled) {
          toastService.success("Login successful, please verify OTP");
          setTempEmail(values.email);
          navigate("/auth/otp");
        } else {
          setTokens(data.accessToken, data.refreshToken);
          toastService.success("Login successful");
          navigate("/dashboard");
        }
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(error, "Login failed. Please try again."),
        );
      },
    });
  };

  return (
    <AuthLayout>
      <AuthCard
        title="Login"
        subtitle={
          <div className="text-primary-500 italic font-600 text-[13px] sm:text-[15px] font-montserrat">
            Where merchants manage smarter
          </div>
        }
      >
        {isBlocked ? (
          <OtpBlockBanner
            countdown={countdown}
            title="Account Locked"
            message={`Your account has been temporarily locked due to multiple incorrect PIN attempts. Please try again in ${countdown}.`}
          />
        ) : (
          banner && (
            <CustomNotificationBanner
              variant={variant}
              title={title}
              message={message}
              mb="md"
              onClose={dismissBanner}
            />
          )
        )}
        <form
          className="flex flex-col gap-2 md:gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="email"
            control={control}
            rules={{ validate: (v) => loginValidators.email(v) ?? true }}
            render={({ field, fieldState }) => (
              <CustomTextInput
                label="Email:"
                radius="md"
                type="email"
                disabled={isPending || isBlocked}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{ validate: (v) => loginValidators.password(v) ?? true }}
            render={({ field, fieldState }) => (
              <CustomPasswordInput
                label="Password:"
                radius="md"
                minLength={8}
                disabled={isPending || isBlocked}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />
          {forgotPassword}
          <CustomButton
            type="submit"
            radius="xl"
            size="sm"
            fullWidth
            disabled={isPending || isBlocked || hasFormErrors}
            loading={isPending}
          >
            Login
          </CustomButton>
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default LoginPage;
